/**
 * @file SkuSelectField.tsx
 * @description Первое поле панели приёмки и списания: скан артикула или
 * «Подбор» (phase19.13 этап 0; на `ScanField` переведено в phase19.24 этап 1,
 * шаг 1).
 *
 * Раскладка одна на все четыре документа: `ScanField` и кнопка «Подбор» в
 * одной строке, под ними — одна подпись о том, что делает ввод. До контракта
 * здесь стоял свой `input` со своими отступами, а в сборке рядом жил
 * `ScanField` — два разных поля скана на соседних экранах.
 *
 * Два состояния контрола:
 *   - пусто → скан/ввод артикула (Enter завершает пачку символов) и «Подбор»;
 *   - выбрано → карточка «Товар — фасовка / артикул» и сброс крестиком.
 *
 * Распознавание идёт и по Enter, и по уходу из поля: кладовщик набирает
 * артикул руками и тянется мышкой к «Количеству», а Enter не жмёт — до фикса
 * форма молча оставалась пустой (замечание ревью 2026-09-20).
 *
 * Фокус после распознавания переводит ФОРМА эффектом по выбранной фасовке:
 * колбэк отрабатывал до перерисовки, когда следующее поле ещё `disabled`, а
 * `ScanField` уже размонтирован, и фокус улетал в `body`.
 *
 * После «Подбора» фокус ведёт сам диалог через `nextFieldRef`: base-ui
 * возвращает фокус позже нашего `focus()`, и курсор падал в `body`. Поэтому
 * диалог рендерится ВСЕГДА, в обоих состояниях контрола: размонтируй мы его
 * вместе с полем скана — закрытие прошло бы без возврата фокуса (дефект
 * повторного QA 2026-09-20).
 */

import { ListPlus } from "lucide-react";
import { useRef, useState, type FocusEvent, type RefObject } from "react";

import { SkuSelectedCard } from "./SkuSelectedCard";
import { WmsSkuSelectDialog } from "./WmsSkuSelectDialog";
import type { WmsPickerSku } from "./WmsSkuPickerDialog";
import { Button } from "../ui/button";
import { ScanField, type ScanFieldHandle } from "../ui/ScanField";
import { formatPickerSkuName, wmsSkusApi } from "../../lib/api-wms";
import { parseScan } from "../../lib/scan-payload";

/** Подпись под полем — одна на приёмку, списание и перемещение. */
const HINT = "Скан или точный артикул выбирает фасовку, другой текст — поиск";

interface Props {
  /** Выбранная фасовка (null — поле пустое). */
  value: WmsPickerSku | null;
  onChange: (sku: WmsPickerSku | null) => void;
  /** Артикулы, уже добавленные в документ, — в «Подборе» будут серыми. */
  alreadyAddedIds?: string[];
  /** Переход к уже добавленной строке документа (прокрутка + подсветка). */
  onGoToExisting?: (skuId: string) => void;
  /**
   * phase19.22 (W20: разные сроки — разные строки) — фасовка уже в документе:
   * вместо перехода к строке вызывающий спросит «перейти или новая строка».
   * Передан — перекрывает `onGoToExisting` и для скана, и для «Подбора».
   */
  onExisting?: (sku: WmsPickerSku) => void;
  /** Поле панели после фасовки («Количество») — туда уходит курсор. */
  nextFieldRef?: RefObject<HTMLElement | null>;
  disabled?: boolean;
}

export function SkuSelectField({
  value,
  onChange,
  alreadyAddedIds = [],
  onGoToExisting,
  onExisting,
  nextFieldRef,
  disabled = false,
}: Props) {
  const scanRef = useRef<ScanFieldHandle>(null);
  const [resolving, setResolving] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  /** Ищет фасовку по введённому/сканированному артикулу. */
  async function resolve(code: string) {
    setResolving(true);
    setHint(null);
    try {
      // Этикетка склада (`SKU:CHB…`, DataMatrix с партией) ищется по коду склада
      // (phase19.22, W17-3): сервер находит внутренний код точным совпадением.
      const parsed = parseScan(code);
      const internal = parsed.kind === "sku" || parsed.kind === "label" ? parsed.internalCode : null;
      const page = await wmsSkusApi.search({ search: internal ?? code, limit: 10 });
      const found = page.items;
      const exact = found.find((s) =>
        internal ? s.internalCode === internal : s.code.toLowerCase() === code.toLowerCase(),
      );
      const picked = exact ?? (found.length === 1 ? found[0] : undefined);
      if (picked) {
        if (onExisting && alreadyAddedIds.includes(picked.id)) onExisting(picked);
        else onChange(picked);
        return;
      }
      setHint(
        found.length === 0
          ? `Фасовка «${code}» не найдена`
          : `Найдено ${found.length} совпадений — выберите через «Подбор»`,
      );
    } catch {
      setHint("Не удалось найти фасовку — попробуйте «Подбор»");
    } finally {
      setResolving(false);
    }
  }

  /** Уход из поля: набранный руками артикул не должен пропасть. */
  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    // Фокус ушёл на «Подбор» внутри той же строки — это не «уход из поля».
    if (event.currentTarget.contains(event.relatedTarget)) return;
    const code = (event.target as HTMLInputElement).value?.trim();
    if (!code || resolving || disabled) return;
    void resolve(code);
  }

  return (
    <div className="flex flex-col gap-2">
      {value ? (
        <SkuSelectedCard
          title={formatPickerSkuName(value)}
          subtitle={value.code}
          disabled={disabled}
          onReset={() => onChange(null)}
        />
      ) : (
        <>
          {/* «Подбор» стоит ВНЕ поля и вне `<label>`: клик по кнопке внутри
              подписи браузер трактует как клик по подписи и уводит фокус в
              поле — диалог тут же его терял (урок панели сборки). */}
          {/* На 1024–1279 боковая панель 320px: в одном ряду с «Подбором»
              поле сжималось до ~157px (дефект приёмки 2026-10-02) — там
              «Подбор» встаёт под поле. */}
          <div className="flex min-w-0 gap-2 lg:flex-col xl:flex-row" onBlur={handleBlur}>
            <div className="min-w-0 flex-1">
              <ScanField
                ref={scanRef}
                busy={resolving}
                disabled={disabled}
                helperText={HINT}
                onSubmit={(code) => void resolve(code)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              // preventDefault: кнопка стоит внутри `<label>` поля, и без него
              // браузер трактует клик как клик по подписи и уводит фокус в input.
              onClick={(e) => {
                e.preventDefault();
                setPickerOpen(true);
              }}
              disabled={disabled}
              className="shrink-0 self-start"
              title="Открыть список фасовок"
            >
              <ListPlus className="size-4" />
              <span className="ml-1.5">Подбор</span>
            </Button>
          </div>
          {/* Неудачное разрешение — отдельной строкой цветом внимания:
              подсказка под полем остаётся инструкцией, а не мигает ошибкой. */}
          {hint ? <p className="text-xs text-warning">{hint}</p> : null}
        </>
      )}

      <WmsSkuSelectDialog
        nextFieldRef={nextFieldRef}
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          scanRef.current?.focus();
        }}
        alreadyAddedIds={alreadyAddedIds}
        onGoToExisting={(skuId, sku) => {
          setPickerOpen(false);
          if (onExisting) onExisting(sku);
          else onGoToExisting?.(skuId);
        }}
        onSelect={(sku) => {
          setPickerOpen(false);
          onChange(sku);
        }}
      />
    </div>
  );
}
